import { useEffect, useState } from 'react';

import { useAtomValue, useSetAtom } from '~shared/lib/atom-state';

import {
  MobileAuthTheme,
  ResolvedMobileAuthTheme,
  getSystemTheme,
  isMobileAuthAtom,
  mobileAuthThemeAtom,
  resolveMobileAuthTheme,
} from './atoms';

export const useIsMobileAuth = (): boolean => useAtomValue(isMobileAuthAtom);

export const useMobileAuthTheme = (): MobileAuthTheme => useAtomValue(mobileAuthThemeAtom);

export const useResolvedMobileAuthTheme = (): ResolvedMobileAuthTheme => {
  const stored = useAtomValue(mobileAuthThemeAtom);
  const [systemTheme, setSystemTheme] = useState<ResolvedMobileAuthTheme>(() => getSystemTheme());

  useEffect(() => {
    if (stored !== 'system') {
      return;
    }

    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }

    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (event: MediaQueryListEvent) =>
      setSystemTheme(event.matches ? 'dark' : 'light');

    setSystemTheme(media.matches ? 'dark' : 'light');

    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', onChange);

      return () => media.removeEventListener('change', onChange);
    }

    media.addListener(onChange);

    return () => media.removeListener(onChange);
  }, [stored]);

  return stored === 'system' ? systemTheme : resolveMobileAuthTheme(stored);
};

export const useSetMobileAuth = () => {
  const setFlag = useSetAtom(isMobileAuthAtom);
  const setTheme = useSetAtom(mobileAuthThemeAtom);

  return (enabled: boolean, theme: MobileAuthTheme = 'light') => {
    setFlag(enabled);
    setTheme(theme);
  };
};

export const useClearMobileAuth = () => {
  const setFlag = useSetAtom(isMobileAuthAtom);
  const setTheme = useSetAtom(mobileAuthThemeAtom);

  return () => {
    setFlag(false);
    setTheme('light');
  };
};

/**
 * Synchronous read — outside of React tree (e.g. inside imperative handlers).
 */
export const readIsMobileAuth = (): boolean => localStorage.getItem('mobileAuth') === 'true';

export const readResolvedMobileAuthTheme = (): ResolvedMobileAuthTheme => {
  const raw = localStorage.getItem('mobileAuthTheme');
  // accept JSON-quoted ("dark"), single-quoted ('dark') or raw (dark) values
  const parsed = raw ? raw.replace(/^['"]|['"]$/g, '') : raw;

  if (parsed === 'dark') {
    return 'dark';
  }

  if (parsed === 'system') {
    return getSystemTheme();
  }

  return 'light';
};
